import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { sendCustomEmail } from '../../Redux/Action/EmailHistoryAction';
import { clearSendEmailStatus } from '../../Redux/Slices/EmailHistorySlice';

const SendCustomEmailModal = ({ show, userId, userName, userEmail, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { sendEmailLoading, sendEmailError, sendEmailSuccess } = useSelector(
    (state) => state.emailHistory
  );

  const [activeTab, setActiveTab] = useState('compose');

  const [formData, setFormData] = useState({
    subject: '',
    content: '',
    useTemplate: true
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (show) {
      setActiveTab('compose');
      setFormData({
        subject: '',
        content: '',
        useTemplate: true
      });
      setErrors({});
      dispatch(clearSendEmailStatus());
    }
  }, [show, dispatch]);

  useEffect(() => {
    if (sendEmailSuccess) {
      onSuccess && onSuccess();
    }
  }, [sendEmailSuccess, onSuccess]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    } else if (formData.subject.length > 200) {
      newErrors.subject = 'Subject must be less than 200 characters';
    }

    // For ReactQuill content, we need to check if there's actual text content
    // Strip HTML tags to check for actual content
    const textContent = formData.content.replace(/<[^>]*>/g, '').trim();
    if (!textContent) {
      newErrors.content = 'Email content is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    dispatch(sendCustomEmail({
      userId,
      emailData: {
        subject: formData.subject.trim(),
        content: formData.content.trim(),
        useTemplate: formData.useTemplate
      }
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(4px)',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '1400px',
        height: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
        border: '1px solid #e9ecef',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '24px 28px',
          background: 'linear-gradient(135deg, #059669 0%, #0d9488 100%)',
          color: 'white',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className="fas fa-paper-plane" style={{ fontSize: '18px' }}></i>
            </div>
            <div>
              <h3 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: '600' }}>Send Custom Email</h3>
              <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>Compose and send a personalized email message</p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              padding: '8px',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.3)'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Tabs */}
        <div style={{ 
          borderBottom: '1px solid #d1fae5',
          backgroundColor: '#f0fdf4',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', padding: '0 28px' }}>
            <button
              onClick={() => setActiveTab('compose')}
              style={{
                padding: '12px 20px',
                border: 'none',
                backgroundColor: 'transparent',
                color: activeTab === 'compose' ? '#059669' : '#6b7280',
                fontWeight: activeTab === 'compose' ? '600' : '500',
                fontSize: '14px',
                cursor: 'pointer',
                borderBottom: activeTab === 'compose' ? '2px solid #059669' : '2px solid transparent',
                transition: 'all 0.2s ease'
              }}
            >
              <i className="fas fa-edit" style={{ marginRight: '8px' }}></i>
              Compose
            </button>
            <button
              onClick={() => setActiveTab('preview')}
              style={{
                padding: '12px 20px',
                border: 'none',
                backgroundColor: 'transparent',
                color: activeTab === 'preview' ? '#059669' : '#6b7280',
                fontWeight: activeTab === 'preview' ? '600' : '500',
                fontSize: '14px',
                cursor: 'pointer',
                borderBottom: activeTab === 'preview' ? '2px solid #059669' : '2px solid transparent',
                transition: 'all 0.2s ease'
              }}
            >
              <i className="fas fa-eye" style={{ marginRight: '8px' }}></i>
              Full Preview
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {activeTab === 'compose' ? (
            <div style={{ 
              padding: '28px', 
              background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdfa 100%)',
              flex: 1,
              overflowY: 'auto'
            }}>
              {/* Recipient Info Card */}
              <div style={{ 
                backgroundColor: 'white', 
                padding: '20px', 
                borderRadius: '12px', 
                marginBottom: '24px',
                border: '2px solid #10b981',
                boxShadow: '0 2px 4px rgba(16,185,129,0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <i className="fas fa-user" style={{ color: 'white', fontSize: '14px' }}></i>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', color: '#065f46', marginBottom: '2px', fontWeight: '500' }}>
                      Sending to:
                    </div>
                    <div style={{ fontWeight: '600', color: '#047857', fontSize: '16px', marginBottom: '4px' }}>
                      {userName}
                    </div>
                    {userEmail && (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '6px',
                        fontSize: '14px',
                        color: '#065f46'
                      }}>
                        <i className="fas fa-envelope" style={{ fontSize: '12px' }}></i>
                        <span>{userEmail}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

        <form onSubmit={handleSubmit}>
          {/* Subject Card */}
          <div style={{ 
            backgroundColor: 'white', 
            padding: '20px', 
            borderRadius: '12px', 
            marginBottom: '20px',
            border: '2px solid #10b981',
            boxShadow: '0 2px 4px rgba(16,185,129,0.1)'
          }}>
            <label style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px', 
              fontWeight: '600', 
              color: '#065f46',
              fontSize: '15px'
            }}>
              <i className="fas fa-tag" style={{ color: '#10b981', fontSize: '14px' }}></i>
              Subject *
            </label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              placeholder="Enter email subject"
              maxLength={200}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: `2px solid ${errors.subject ? '#dc3545' : '#d1fae5'}`,
                borderRadius: '8px',
                fontSize: '14px',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s ease',
                backgroundColor: '#f0fdf4'
              }}
              onFocus={(e) => {
                if (!errors.subject) e.target.style.borderColor = '#10b981';
              }}
              onBlur={(e) => {
                if (!errors.subject) e.target.style.borderColor = '#d1fae5';
              }}
            />
            {errors.subject && (
              <div style={{ 
                color: '#dc3545', 
                fontSize: '13px', 
                marginTop: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <i className="fas fa-exclamation-circle" style={{ fontSize: '12px' }}></i>
                {errors.subject}
              </div>
            )}
            <div style={{ 
              fontSize: '12px', 
              color: '#065f46', 
              marginTop: '6px',
              textAlign: 'right'
            }}>
              {formData.subject.length}/200 characters
            </div>
          </div>

          {/* Template Toggle Card */}
          <div style={{ 
            backgroundColor: 'white', 
            padding: '20px', 
            borderRadius: '12px', 
            marginBottom: '20px',
            border: '2px solid #10b981',
            boxShadow: '0 2px 4px rgba(16,185,129,0.1)'
          }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: '600',
              color: '#065f46'
            }}>
              <div style={{
                position: 'relative',
                width: '44px',
                height: '24px',
                backgroundColor: formData.useTemplate ? '#10b981' : '#d1d5db',
                borderRadius: '12px',
                marginRight: '12px',
                transition: 'background-color 0.2s ease'
              }}>
                <input
                  type="checkbox"
                  checked={formData.useTemplate}
                  onChange={(e) => handleInputChange('useTemplate', e.target.checked)}
                  style={{ display: 'none' }}
                />
                <div style={{
                  position: 'absolute',
                  top: '2px',
                  left: formData.useTemplate ? '22px' : '2px',
                  width: '20px',
                  height: '20px',
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  transition: 'left 0.2s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}></div>
              </div>
              <div>
                <div style={{ marginBottom: '2px' }}>Use B4T email template</div>
                <div style={{ fontSize: '12px', color: '#047857', fontWeight: '400' }}>
                  Recommended for consistent branding
                </div>
              </div>
            </label>
            <div style={{ 
              fontSize: '13px', 
              color: '#065f46', 
              marginTop: '12px',
              padding: '12px',
              backgroundColor: '#f0fdf4',
              borderRadius: '6px',
              border: '1px solid #bbf7d0'
            }}>
              <i className="fas fa-info-circle" style={{ marginRight: '6px', color: '#10b981' }}></i>
              {formData.useTemplate 
                ? 'Your content will be wrapped in the standard B4T email template with header and footer'
                : 'Your content will be sent as raw HTML without any template wrapper'
              }
            </div>
          </div>

          {/* Content Card with WYSIWYG Editor */}
          <div style={{ 
            backgroundColor: 'white', 
            padding: '20px', 
            borderRadius: '12px', 
            marginBottom: '20px',
            border: '2px solid #10b981',
            boxShadow: '0 2px 4px rgba(16,185,129,0.1)'
          }}>
            <label style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px', 
              fontWeight: '600', 
              color: '#065f46',
              fontSize: '15px'
            }}>
              <i className="fas fa-edit" style={{ color: '#10b981', fontSize: '14px' }}></i>
              Email Content *
            </label>
            
            <div style={{
              border: `2px solid ${errors.content ? '#dc3545' : '#d1fae5'}`,
              borderRadius: '8px',
              backgroundColor: '#f0fdf4',
              overflow: 'hidden'
            }}>
              <ReactQuill
                value={formData.content}
                onChange={(value) => handleInputChange('content', value)}
                placeholder={formData.useTemplate 
                  ? "Enter your email content here. Use the toolbar for formatting."
                  : "Enter your complete HTML email content here."
                }
                style={{
                  backgroundColor: 'white',
                  minHeight: '300px'
                }}
                theme="snow"
                modules={{
                  toolbar: [
                    [{ 'header': [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ 'color': [] }, { 'background': [] }],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    [{ 'align': [] }],
                    ['link', 'image'],
                    ['clean']
                  ]
                }}
                formats={[
                  'header', 'bold', 'italic', 'underline', 'strike',
                  'color', 'background', 'list', 'bullet', 'align',
                  'link', 'image'
                ]}
              />
            </div>
            
            {errors.content && (
              <div style={{ 
                color: '#dc3545', 
                fontSize: '13px', 
                marginTop: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <i className="fas fa-exclamation-circle" style={{ fontSize: '12px' }}></i>
                {errors.content}
              </div>
            )}
            <div style={{ 
              fontSize: '12px', 
              color: '#065f46', 
              marginTop: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <i className="fas fa-magic" style={{ fontSize: '10px' }}></i>
              Use the toolbar above for rich text formatting. HTML will be generated automatically.
            </div>
          </div>

          {/* Content Preview Card */}
          {(() => {
            const textContent = formData.content.replace(/<[^>]*>/g, '').trim();
            return textContent && (
              <div style={{ 
                backgroundColor: 'white', 
                padding: '20px', 
                borderRadius: '12px', 
                marginBottom: '20px',
                border: '2px solid #10b981',
                boxShadow: '0 2px 4px rgba(16,185,129,0.1)'
              }}>
                <label style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px', 
                  fontWeight: '600', 
                  color: '#065f46',
                  fontSize: '15px'
                }}>
                  <i className="fas fa-eye" style={{ color: '#10b981', fontSize: '14px' }}></i>
                  Content Preview
                </label>
                <div style={{ 
                  border: '2px solid #d1fae5', 
                  borderRadius: '8px',
                  backgroundColor: '#f0fdf4',
                  padding: '16px',
                  maxHeight: '200px',
                  overflow: 'auto'
                }}>
                  <div 
                    dangerouslySetInnerHTML={{ __html: formData.content }}
                    style={{ fontSize: '14px', lineHeight: '1.5' }}
                  />
                </div>
              </div>
            );
          })()}

          {/* Submit Error */}
          {sendEmailError && (
            <div style={{
              color: '#dc3545',
              backgroundColor: '#fef2f2',
              border: '2px solid #fecaca',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className="fas fa-exclamation-triangle" style={{ fontSize: '16px' }}></i>
              <div>
                <div style={{ fontWeight: '600', marginBottom: '4px' }}>Error sending email</div>
                <div>{sendEmailError}</div>
              </div>
            </div>
          )}
        </form>
            </div>
          ) : (
            <div style={{ 
              padding: '28px', 
              background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdfa 100%)',
              flex: 1,
              overflowY: 'auto'
            }}>
              {/* Full Email Preview */}
              <div style={{ 
                backgroundColor: 'white', 
                borderRadius: '12px', 
                border: '2px solid #10b981',
                boxShadow: '0 2px 4px rgba(16,185,129,0.1)',
                overflow: 'hidden'
              }}>
                {/* Email Header */}
                <div style={{
                  padding: '20px',
                  borderBottom: '1px solid #d1fae5',
                  backgroundColor: '#f0fdf4'
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <strong style={{ color: '#065f46', fontSize: '14px' }}>From:</strong>
                    <span style={{ marginLeft: '8px', color: '#047857' }}>no-reply@b4t.com</span>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <strong style={{ color: '#065f46', fontSize: '14px' }}>To:</strong>
                    <span style={{ marginLeft: '8px', color: '#047857' }}>
                      {userName} {userEmail && `<${userEmail}>`}
                    </span>
                  </div>
                  <div>
                    <strong style={{ color: '#065f46', fontSize: '14px' }}>Subject:</strong>
                    <span style={{ marginLeft: '8px', color: '#047857', fontWeight: '600' }}>
                      {formData.subject || 'No subject'}
                    </span>
                  </div>
                </div>

                {/* Email Body */}
                <div style={{ padding: '20px' }}>
                  {formData.useTemplate ? (
                    <div>
                      {/* Template Header */}
                      <div style={{
                        textAlign: 'center',
                        padding: '20px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: '1px solid #e2e8f0'
                      }}>
                        <h2 style={{ 
                          color: '#059669', 
                          margin: '0 0 8px 0',
                          fontSize: '24px',
                          fontWeight: '700'
                        }}>
                          Buddy4Travel
                        </h2>
                        <p style={{ 
                          color: '#64748b', 
                          margin: 0,
                          fontSize: '14px'
                        }}>
                          Your trusted travel companion service
                        </p>
                      </div>

                      {/* Content */}
                      <div style={{ 
                        minHeight: '200px',
                        padding: '20px',
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0'
                      }}>
                        {formData.content ? (
                          <div dangerouslySetInnerHTML={{ __html: formData.content }} />
                        ) : (
                          <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                            Email content will appear here...
                          </p>
                        )}
                      </div>

                      {/* Template Footer */}
                      <div style={{
                        textAlign: 'center',
                        padding: '20px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '8px',
                        marginTop: '20px',
                        border: '1px solid #e2e8f0'
                      }}>
                        <p style={{
                          color: '#475569',
                          fontWeight: '600',
                          margin: '0 0 4px 0',
                          fontSize: '14px'
                        }}>
                          © {new Date().getFullYear()} Buddy4Travel. All rights reserved.
                        </p>
                        <p style={{ 
                          color: '#9ca3af', 
                          margin: 0,
                          fontSize: '11px'
                        }}>
                          This email was sent from the B4T Admin Panel
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div style={{ minHeight: '300px' }}>
                      {formData.content ? (
                        <div dangerouslySetInnerHTML={{ __html: formData.content }} />
                      ) : (
                        <p style={{ color: '#9ca3af', fontStyle: 'italic', textAlign: 'center', paddingTop: '100px' }}>
                          Email content will appear here...
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ 
          padding: '20px 28px', 
          borderTop: '1px solid #d1fae5',
          background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          flexShrink: 0
        }}>
          <button
            type="button"
            onClick={onClose}
            disabled={sendEmailLoading}
            style={{
              padding: '12px 24px',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: sendEmailLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              opacity: sendEmailLoading ? 0.7 : 1,
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minHeight: '44px'
            }}
            onMouseOver={(e) => {
              if (!sendEmailLoading) e.target.style.backgroundColor = '#4b5563';
            }}
            onMouseOut={(e) => {
              if (!sendEmailLoading) e.target.style.backgroundColor = '#6b7280';
            }}
          >
            <i className="fas fa-times"></i>
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={sendEmailLoading}
            style={{
              padding: '12px 24px',
              background: sendEmailLoading ? '#6b7280' : 'linear-gradient(135deg, #059669 0%, #0d9488 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: sendEmailLoading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              opacity: sendEmailLoading ? 0.7 : 1,
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: sendEmailLoading ? 'none' : '0 2px 4px rgba(5,150,105,0.2)',
              minHeight: '44px'
            }}
            onMouseOver={(e) => {
              if (!sendEmailLoading) {
                e.target.style.background = 'linear-gradient(135deg, #047857 0%, #0f766e 100%)';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 8px rgba(5,150,105,0.3)';
              }
            }}
            onMouseOut={(e) => {
              if (!sendEmailLoading) {
                e.target.style.background = 'linear-gradient(135deg, #059669 0%, #0d9488 100%)';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(5,150,105,0.2)';
              }
            }}
          >
            {sendEmailLoading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Sending...
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane"></i>
                Send Email
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendCustomEmailModal;